import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, catchError, map, Observable, of } from 'rxjs';
import { environment } from 'src/environments/environment';
import { IWsResult } from '../models/ws_result.model';
import { ErrorHandlerService } from './shared/error-handler.service';
import { CurrentUser, SessionService } from './shared/session.service';
import Swal from 'sweetalert2';
import { Helpers } from '../models/helpers';
import { SCREEN_KIOSK_SESSION } from '../models/screen-kiosk.config';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  permissionUsed: BehaviorSubject<Helpers.Permission> = new BehaviorSubject<Helpers.Permission>(Helpers.PermisionNone);

  public isUser: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public currentUserSubject: BehaviorSubject<any>;
  public currentUser: Observable<IWsResult> | undefined;
  public isScreen: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    null
  );
  public screenUserSubject: BehaviorSubject<IWsResult>;
  public screen: Observable<IWsResult> | undefined;

  stateUrl: string = 'dashboard';
  url: string = environment.urlServer;

  public notificationPushData: BehaviorSubject<string> =
    new BehaviorSubject<string>(null);

  constructor(
    private http: HttpClient,
    private router: Router,
    private errorHandlerServ: ErrorHandlerService,
    private session: SessionService,
  ) {
    this.isLoggedIn();
  }

  async loadToken() {
    if (await this.isLoggedIn()) {
      return true;
    }
    else {
      this.permissionUsed.next(Helpers.PermisionNone);
      return false;
    }
  }

  isLoggedIn() {
    const account = this.session.getCurrentUser() ?? {};
    this.currentUserSubject = new BehaviorSubject<IWsResult>(account as IWsResult);

    if (this.session.isAuthenticated()) {
      this.isUser.next(true);
      this.getScreenEmail();
      this.currentUser = this.currentUserSubject.asObservable();
      //leave as is to work with normal app
      this.permissionUsed.next(account.ac_user_type_cod as Helpers.Permission);
      //to work on productator/manufacturer account
      // this.permissionUsed.next(Helpers.PermisionAdmin);
      console.log("permission is ", this.permissionUsed.value)
      return true;
    }
    else {
      this.isUser.next(false);
      return false
    }

  }

  getScreenEmail() {
    const currentScreen = this.session.getCurrentUser() ?? {};
    if (currentScreen.ac_user_type_cod === 3) {
      this.isScreen.next(true);
    } else {
      this.isScreen.next(false);
    }
  }

  public get currentUserValue(): any {
    return this.currentUserSubject.value;
  }

  login(email: string, password: string, ip: string) {
    let loginInfo = { email: email, password: password, ip_address: ip };
    return this.http.post<any>(`${this.url}/login/`, loginInfo).pipe(
      map((result: IWsResult) => {
        if (result.success && result.data && result.data.user_ID != 0) {
          console.log('ac_user_type_cod', result.data.ac_user_type_cod);
          if (
            result?.data?.ac_user_type_cod > 1 &&
            (result?.data?.ac_user_type_cod !== 3 && result?.data?.ac_user_type_cod !== 4)
          ) {
            Swal.fire({
              icon: 'error',
              title: 'Accesso negato!',
              text: 'L`utente non ha i permessi necessari!',
              timer: 1500,
              timerProgressBar: true,
            });
            return;
          }
          this.permissionUsed.next(result.data?.ac_user_type_cod)
          this.isUser.next(true);
          this.session.setCurrentUser(result.data);
          this.getScreenEmail();
          if (result.data?.ac_user_type_cod === Helpers.PermisionScreen) {
            this.session.setScreenCredentials({ email, password });
          }
        }
        this.currentUserSubject.next(result.data);
        return result;
      }),
      catchError(this.errorHandlerServ.handleError('login'))
    );
  }

  /**
   * Re-login silenzioso per un account Screen (kiosk, es. monitor
   * magazzino) usando le credenziali salvate al login precedente.
   *
   * Usato da `AuthInterceptor` quando il backend risponde con
   * msg_code 7 (token scaduto): questi schermi sono incustoditi 24/7,
   * quindi al posto del logout+redirect a /login (che li lascerebbe
   * bloccati finche' qualcuno non va fisicamente a rifare login) si
   * tenta di riautenticarsi da soli con le stesse credenziali.
   *
   * Ritorna `false` (senza lanciare errori) se non ci sono credenziali
   * salvate o se il re-login fallisce, cosi' il chiamante puo' ripiegare
   * sul logout normale.
   */
  /**
   * Ripristina la sessione del monitor magazzino dalla configurazione
   * kiosk hardcoded (vedi `SCREEN_KIOSK_SESSION`): persiste l'utente in
   * localStorage e riallinea lo stato osservabile (permissionUsed,
   * isScreen, currentUserSubject).
   *
   * Usata da `ScreenTokenGuard` quando arriva un token nei query params
   * dell'URL kiosk e da `AuthInterceptor` come primo tentativo di
   * recupero su msg_code 7. `overrides` permette al guard di dare
   * precedenza ai valori arrivati nell'URL.
   */
  applyKioskSession(overrides: Partial<CurrentUser> = {}): void {
    this.session.setCurrentUser({
      ac_email: SCREEN_KIOSK_SESSION.email,
      account_id: SCREEN_KIOSK_SESSION.accountId,
      ac_token: SCREEN_KIOSK_SESSION.token,
      ac_user_type_cod: Helpers.PermisionScreen,
      ...overrides,
    });
    this.isLoggedIn();
  }

  reloginScreen(): Observable<boolean> {
    const credentials = this.session.getScreenCredentials();
    if (!credentials) {
      return of(false);
    }
    return this.login(credentials.email, credentials.password, '').pipe(
      map((result: IWsResult) => !!result?.success),
      catchError(() => of(false)),
    );
  }

  checkPass(email: string, password: string, ip: string) {
    let loginInfo = {
      email: email,
      password: password,
      ip_address: ip,
    };
    return this.http.post<any>(`${this.url}/login/`, loginInfo).pipe(
      map((result: IWsResult) => {
        if (result.success && result.data && result.data.user_ID != 0) {
          this.isUser.next(true);
        }
        this.currentUserSubject.next(result.data);
        return result;
      }),
      catchError(this.errorHandlerServ.handleError('login'))
    );
  }

  clearUser() {
    this.currentUser = undefined;
    this.isUser.next(false);
    this.session.clear();
    this.currentUserSubject.next({} as IWsResult);
  }

  logout() {
    this.clearUser();
    this.router.navigateByUrl('/login', { replaceUrl: true });
  }

  resetPassword(username: string): Observable<IWsResult> {
    return this.http
      .post<IWsResult>(`${this.url}/reset_password/`, username)
      .pipe(
        catchError(
          this.errorHandlerServ.handleError<IWsResult>('resetPassword')
        )
      );
  }

  changePassword(
    old_password: string,
    new_password: string,
    rep_password: string
  ): Observable<IWsResult> {
    let accountInfo = {
      old_password: old_password,
      new_password: new_password,
      rep_password: rep_password,
    };
    return this.http
      .post<IWsResult>(`${this.url}/put_password/`, accountInfo)
      .pipe(
        catchError(
          this.errorHandlerServ.handleError<IWsResult>('changePassword')
        )
      );
  }
}
